import { useState, useEffect } from 'react'
import {
  Briefcase,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  FileText,
  ChevronDown,
  ChevronUp,
  User,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'

interface Application {
  id: number
  full_name: string
  email: string
  current_title: string
  specializations: string
  years_experience: number
  motivation: string
  linkedin_url?: string | null
  status: string
  review_notes?: string | null
  created_at: string
}

const payTiers = [
  { id: 1, name: 'Standard', rate: 50, description: 'General interview coaching' },
  { id: 2, name: 'Technical', rate: 75, description: 'Technical/engineering interviews' },
  { id: 3, name: 'Executive', rate: 100, description: 'Senior/executive level interviews' },
]

export default function AdminApplications() {
  const [applications, setApplications] = useState<Application[]>([])
  const [filter, setFilter] = useState('all')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [selectedTier, setSelectedTier] = useState<Record<number, string>>({})
  const [declineReason, setDeclineReason] = useState<Record<number, string>>({})
  const [tierError, setTierError] = useState<Record<number, boolean>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchApplications()
  }, [])

  const fetchApplications = async () => {
    try {
      const data = await adminApi.getApplications()
      setApplications(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to fetch applications:', err)
    } finally {
      setLoading(false)
    }
  }

  const filteredApps = applications.filter((app) => {
    if (filter === 'all') return true
    return app.status === filter
  })

  const handleApprove = async (id: number) => {
    if (!selectedTier[id]) {
      setTierError((prev) => ({ ...prev, [id]: true }))
      return
    }
    setTierError((prev) => ({ ...prev, [id]: false }))
    try {
      const tier = payTiers.find((t) => t.id === parseInt(selectedTier[id]))?.name?.toLowerCase()
      await adminApi.approveApplication(id, { pay_tier: tier })
      setApplications((apps) =>
        apps.map((app) =>
          app.id === id
            ? { ...app, status: 'approved', pay_tier: tier || undefined }
            : app
        )
      )
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to approve application')
    }
  }

  const handleDecline = async (id: number) => {
    try {
      await adminApi.declineApplication(id, {
        reason: declineReason[id] || '',
      })
      setApplications((apps) =>
        apps.map((app) =>
          app.id === id ? { ...app, status: 'declined' } : app
        )
      )
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to decline application')
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="badge badge-warning">
            <Clock size={12} /> Pending
          </span>
        )
      case 'approved':
        return (
          <span className="badge badge-success">
            <CheckCircle size={12} /> Approved
          </span>
        )
      case 'declined':
        return (
          <span className="badge badge-danger">
            <XCircle size={12} /> Declined
          </span>
        )
      default:
        return null
    }
  }

  const pendingCount = applications.filter((a) => a.status === 'pending').length

  if (loading) {
    return <div className="loading-screen">Loading applications...</div>
  }

  return (
    <div>
      <div
        className="page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <h1 className="page-title">Interviewer Applications</h1>
          <p className="page-subtitle">
            Review and manage interviewer applications
          </p>
        </div>
        {pendingCount > 0 && (
          <span
            className="badge badge-warning"
            style={{ fontSize: 14, padding: '8px 16px' }}
          >
            {pendingCount} pending
          </span>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 300px',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div>
          {/* Filter Tabs */}
          <div className="tabs" style={{ marginBottom: 24 }}>
            {['all', 'pending', 'approved', 'declined'].map((f) => (
              <button
                key={f}
                className={`tab ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === 'pending' && pendingCount > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      background: 'var(--warning)',
                      color: 'white',
                      borderRadius: 10,
                      padding: '1px 8px',
                      fontSize: 11,
                    }}
                  >
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Applications List */}
          {filteredApps.length === 0 ? (
            <div className="empty-state">
              <Briefcase size={48} />
              <h3>No applications found</h3>
            </div>
          ) : (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              {filteredApps.map((app) => (
                <div
                  key={app.id}
                  className="card"
                  style={{ padding: 0, overflow: 'hidden' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      padding: 20,
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      setExpandedId(
                        expandedId === app.id ? null : app.id
                      )
                    }
                  >
                    <div className="avatar-lg">
                      {app.full_name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 16, fontWeight: 600 }}>
                        {app.full_name}
                      </h3>
                      <p
                        style={{
                          fontSize: 13,
                          color: 'var(--text-muted)',
                        }}
                      >
                        {app.specializations} | {app.years_experience}{' '}
                        years experience
                      </p>
                    </div>
                    {getStatusBadge(app.status)}
                    {expandedId === app.id ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </div>

                  {expandedId === app.id && (
                    <div
                      style={{
                        padding: '0 20px 20px',
                        borderTop: '1px solid var(--border)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 12,
                          paddingTop: 16,
                        }}
                      >
                        {/* Email */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                          <Mail size={16} style={{ color: 'var(--text-muted)' }} />
                          {app.email}
                        </div>

                        {/* LinkedIn — only if returned by API */}
                        {app.linkedin_url && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                            <User size={16} style={{ color: 'var(--text-muted)' }} />
                            <a
                              href={app.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'var(--primary-navy)' }}
                            >
                              LinkedIn Profile
                            </a>
                          </div>
                        )}

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 8,
                            fontSize: 14,
                          }}
                        >
                          <FileText
                            size={16}
                            style={{
                              color: 'var(--text-muted)',
                              marginTop: 2,
                              flexShrink: 0,
                            }}
                          />
                          {app.motivation}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontSize: 13,
                            color: 'var(--text-muted)',
                          }}
                        >
                          <Clock size={14} />
                          Applied: {formatDate(app.created_at)}
                        </div>
                      </div>

                      {app.status === 'pending' && (
                        <div
                          style={{
                            marginTop: 16,
                            paddingTop: 16,
                            borderTop: '1px solid var(--border)',
                          }}
                        >
                          <div
                            style={{
                              marginBottom: 12,
                              padding: 12,
                              borderRadius: 8,
                              border: tierError[app.id]
                                ? '1.5px solid var(--danger)'
                                : '1.5px solid var(--border)',
                              background: tierError[app.id]
                                ? '#fff5f5'
                                : 'var(--accent-light)',
                            }}
                          >
                            <label
                              style={{ fontWeight: 500, fontSize: 14, display: 'block', marginBottom: 8 }}
                            >
                              Pay Tier <span style={{ color: 'var(--danger)' }}>*</span>
                            </label>
                            <select
                              className="form-input"
                              style={{
                                width: '100%',
                                borderColor: tierError[app.id] ? 'var(--danger)' : undefined,
                              }}
                              value={selectedTier[app.id] || ''}
                              onChange={(e) => {
                                setSelectedTier({ ...selectedTier, [app.id]: e.target.value })
                                if (e.target.value) setTierError((prev) => ({ ...prev, [app.id]: false }))
                              }}
                            >
                              <option value="">Select tier...</option>
                              {payTiers.map((tier) => (
                                <option key={tier.id} value={tier.id}>
                                  {tier.name} - ${tier.rate}/hr
                                </option>
                              ))}
                            </select>
                            {tierError[app.id] && (
                              <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>
                                Please select a pay tier before approving.
                              </p>
                            )}
                          </div>
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-input"
                              placeholder="Decline reason (optional)"
                              value={declineReason[app.id] || ''}
                              onChange={(e) =>
                                setDeclineReason({
                                  ...declineReason,
                                  [app.id]: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: 8,
                            }}
                          >
                            <button
                              className="btn btn-success"
                              style={{
                                padding: '8px 16px',
                                fontSize: 13,
                              }}
                              onClick={() => handleApprove(app.id)}
                            >
                              <CheckCircle size={14} /> Approve
                            </button>
                            <button
                              className="btn btn-danger"
                              style={{
                                padding: '8px 16px',
                                fontSize: 13,
                              }}
                              onClick={() => handleDecline(app.id)}
                            >
                              <XCircle size={14} /> Decline
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar: Pay Tiers */}
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
            Pay Tiers
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {payTiers.map((tier) => (
              <div
                key={tier.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 12,
                  background: 'var(--accent-light)',
                  borderRadius: 8,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {tier.name}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-muted)',
                    }}
                  >
                    {tier.description}
                  </div>
                </div>
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  ${tier.rate}/hr
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
